/**
 * useOrgBranding
 * ==============
 * Owns the single per-page org-branding fetch for the public auth pages
 * (login / forgot-password / reset-password).
 *
 * Two resolution modes:
 *  - `orgSlug` present (path routes like /:orgSlug/login) → fetch branding by slug.
 *  - `orgSlug` absent (bare /login on a branded domain) → fetch branding by HOST
 *    (domain-based login branding). The backend matches the request host against
 *    the registered-domain allowlist; an unmapped host (zcortexai.com, localhost)
 *    resolves to the neutral default.
 *
 * Exposes `{ branding, resolved }`:
 *  - `branding` — the org branding payload ({ name, slug, logo_url, colors }) or null.
 *  - `resolved` — false only while a fetch is in flight. Consumers gate the logo on
 *    this flag so the default logo never flashes before the org logo. Starts true
 *    only when a cache hit (including a cached "no match") lets us paint instantly.
 *
 * A sessionStorage cache seeds `branding` for instant first paint on repeat visits;
 * the network fetch still runs to refresh. Slug and host caches use distinct keys.
 * A no-match result is cached as a sentinel so repeat visits to an unmapped host
 * resolve instantly instead of re-showing the placeholder.
 */

import { useEffect, useState } from 'react';
import { getOrgBranding, getOrgBrandingByHost } from '../api/public';

const NO_MATCH = { __none: true };

const slugKey = (slug) => `org-branding:${slug}`;
const hostKey = (host) => `org-branding:host:${host}`;

const readCache = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCache = (key, data) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch {
    // sessionStorage unavailable (private mode / quota) — non-fatal.
  }
};

// A cached value may be a branding object, the NO_MATCH sentinel, or null.
// Normalize the sentinel to null branding for consumers.
const brandingFromCache = (cached) =>
  cached && cached.__none ? null : cached;

export const useOrgBranding = (orgSlug) => {
  const key = orgSlug ? slugKey(orgSlug) : hostKey(window.location.hostname);
  const cached = readCache(key);
  const [branding, setBranding] = useState(brandingFromCache(cached));
  // A cache hit (real branding OR a cached no-match) lets us render right away
  // while the background refresh runs; otherwise wait for the fetch to settle.
  const [resolved, setResolved] = useState(Boolean(cached));

  useEffect(() => {
    let cancelled = false;

    const seeded = readCache(key);
    setBranding(brandingFromCache(seeded));
    setResolved(Boolean(seeded));

    const fetchBranding = orgSlug
      ? getOrgBranding(orgSlug)
      : getOrgBrandingByHost();

    fetchBranding
      .then((res) => {
        if (cancelled) return;
        if (res?.data) {
          setBranding(res.data);
          writeCache(key, res.data);
        } else {
          // Explicit no-match (unmapped host / null data) → neutral default.
          setBranding(null);
          writeCache(key, NO_MATCH);
        }
      })
      .catch(() => {
        // 404 or network error — silent fallback to the neutral default.
        if (!cancelled) setBranding(null);
      })
      .finally(() => {
        if (!cancelled) setResolved(true);
      });

    return () => {
      cancelled = true;
    };
  }, [orgSlug, key]);

  return { branding, resolved };
};

export default useOrgBranding;
